import { CharacterCounter, cn, IconButton } from '@kanbu/chatbot-ui';
import { t } from '@lingui/core/macro';
import { Loader2, Send, Square } from 'lucide-react';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useBoundStore } from '@/store/store';

import {
  DisclaimerAcknowledgement,
  type DisclaimerAcknowledgementRef,
} from './DisclaimerAcknowledgement';

type InputBoxProps = {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onAbort?: () => void;
  value: string;
  isLoading?: boolean;
  isFetching?: boolean;
  disabledInput?: boolean;
  maxLength?: number;
  disclaimerContent?: string;
  requireAcknowledgement?: boolean;
};

export const InputBox = memo(
  ({
    onSubmit,
    onChange,
    onAbort,
    value,
    isLoading,
    isFetching,
    disabledInput,
    maxLength = 2000,
    disclaimerContent,
    requireAcknowledgement,
  }: InputBoxProps) => {
    const deferredValue = useDeferredValue(value);
    const { setIsInFAQLoop } = useBoundStore(
      useShallow(state => ({
        setIsInFAQLoop: state.setIsInFAQLoop,
      })),
    );
    const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(true);
    const disclaimerRef = useRef<DisclaimerAcknowledgementRef>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isSubmitDisabled =
      ((isLoading || isFetching) && !onAbort) ||
      (deferredValue.length < 1 && !isLoading && !isFetching);

    /**
     * Handle the submission of the input box.
     * If the input box is loading, it will abort the submission.
     * If the disclaimer is required but not acknowledged, focus the disclaimer checkbox.
     */
    const handleSubmit = useCallback(
      (event: React.FormEvent) => {
        // Check if disclaimer needs acknowledgement
        if (requireAcknowledgement && !disclaimerAcknowledged) {
          event.preventDefault();
          disclaimerRef.current?.triggerAttentionAnimation();
          disclaimerRef.current?.scrollIntoView();

          setTimeout(() => {
            disclaimerRef.current?.focus();
          }, 300);

          return;
        }

        // Don't do anything if there is no onAbort function
        if (!onAbort) {
          return;
        }

        // Prevent submission and abort
        if (isLoading || isFetching) {
          event.preventDefault();
          onAbort();
        }

        onSubmit(event as FormEvent<HTMLFormElement>);
        setIsInFAQLoop(false);
      },
      [
        requireAcknowledgement,
        disclaimerAcknowledged,
        onAbort,
        isLoading,
        isFetching,
        onSubmit,
        setIsInFAQLoop,
      ],
    );

    /**
     * Handles the auto-resizing of the textarea based on the content.
     */
    useEffect(() => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value]);

    /**
     * Submits the message when the Enter key is pressed without the Shift key.
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabledInput || isSubmitDisabled || isFetching) {
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    return (
      <form onSubmit={handleSubmit} className='flex flex-col gap-2'>
        <div className='relative'>
          <div
            className={cn(
              'grid cursor-text gap-2 overflow-hidden rounded-3xl bg-chat-input-background text-chat-input-foreground placeholder-chat-placeholder p-2 relative',
              disabledInput && 'opacity-70',
            )}
            onClick={() => !disabledInput && textareaRef.current?.focus()}
          >
            <textarea
              ref={textareaRef}
              rows={1}
              className='no-scrollbar max-h-[150px] min-h-8 flex-1 resize-none overflow-y-auto border-none bg-transparent pl-2 pr-11 pt-1 outline-none disabled:cursor-not-allowed'
              placeholder={
                isFetching
                  ? t`Waiting for a response...`
                  : isLoading
                    ? t`Loading conversation...`
                    : t`Type your question`
              }
              maxLength={maxLength}
              value={value}
              disabled={disabledInput}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              data-testid='input-box'
            />
          </div>

          <div className='absolute inset-y-0 right-0 flex items-start pr-1 pt-1'>
            <IconButton
              type='submit'
              variant='primary'
              circle
              size='lg'
              disabled={isSubmitDisabled}
              data-testid='send-button'
              data-is-loading={isLoading || isFetching}
            >
              {isLoading && !isFetching ? (
                <Loader2 className='animate-spin' />
              ) : (isLoading || isFetching) && onAbort ? (
                <Square />
              ) : (
                <Send />
              )}
            </IconButton>
          </div>

          <div className='absolute bottom-0.5 right-3'>
            <CharacterCounter max={maxLength} current={deferredValue?.length} />
          </div>
        </div>

        <DisclaimerAcknowledgement
          ref={disclaimerRef}
          disclaimerContent={disclaimerContent}
          requireAcknowledgement={requireAcknowledgement}
          onAcknowledgementChange={setDisclaimerAcknowledged}
          onFocusRequest={() => textareaRef.current?.focus()}
        />
      </form>
    );
  },
);
